import {Component, computed, effect, inject, OnDestroy, OnInit, signal} from '@angular/core';
import {ActivatedRoute, NavigationEnd, Router} from '@angular/router';
import {RoutesAddonsEnums, RoutesEnums, RoutesNamesEnum} from 'src/app/enums/RoutesEnums';
import {SearchService} from '../../../../services/search/search.service';
import {UrlTermsSymbolsEnum} from '../../../../enums/SeoEnums';
import {DLPageTypeEnum} from '../../../../enums/DataLayerEnums';
import {Breadcrumb} from '../../../../types/Breadcrumb';
import {RelatedLink} from '../../types/RelatedLink';
import {SearchWidgetDataItem} from '../../../../types/SearchWidgetDataItem';
import {SearchState} from '../../../../types/SearchState';
import {ToolsService} from '../../../../services/tools.service';
import {DefaultValuesService} from '../../../../services/default-values.service';
import {Subscription} from 'rxjs';
import {RelatedService} from '../../services/related.service';

@Component({
  selector: 'app-related-category-details',
  templateUrl: './related-category-details.component.html',
})
export class RelatedCategoryDetailsComponent implements OnInit, OnDestroy {
  public categoryType = signal<RoutesAddonsEnums>(null);
  public categorySlug = signal<string>(null);
  public categoryDataKey = signal<string>(null);
  public categoryAddon = signal<UrlTermsSymbolsEnum>(null);
  public categoryTypeName = signal<string>(null);
  public backRouterLink = computed<string[]>(() => {
    if (this.categoryType()) {
      return ['/', RoutesEnums.RELATED, this.categoryType()];
    }
    return ['/', RoutesEnums.RELATED];
  });
  public pageType: DLPageTypeEnum = DLPageTypeEnum.SERP_RELATED_PAGE;
  public breadcrumbs: Breadcrumb[] = [];
  private searchService = inject(SearchService);
  private handledWidgetData = this.searchService.searchWidgetData;
  public selectedItem = computed<SearchWidgetDataItem>(() =>
    this.handledWidgetData()
      ? this.handledWidgetData()[this.categoryDataKey()].find((item) => item.urlPath === this.categorySlug())
      : null,
  );
  private handledPlaces = this.searchService.places;
  public categoryItems = computed<RelatedLink[]>(() => {
    if (
      this.handledWidgetData() &&
      this.handledWidgetData().caregiverUnitTypes &&
      this.handledWidgetData().careServiceTypes &&
      this.handledWidgetData().symptoms &&
      this.categoryType() &&
      this.categoryDataKey() &&
      this.categoryAddon() &&
      this.handledPlaces() &&
      this.selectedItem()
    ) {
      const links: RelatedLink[] = [];

      const selectedItemUrlPathWithAddon: string = `${this.selectedItem().urlPath}-${this.categoryAddon()}`;
      this.handledPlaces().places.forEach((item) => {
        const itemUrlPathWithAddon = `${item.urlPath}-p`;
        const link: RelatedLink = {
          name: item.name,
          routerLink: ['/', RoutesEnums.SEARCH, selectedItemUrlPathWithAddon, itemUrlPathWithAddon],
          title: `Hitta ${this.selectedItem().name} i ${item.name}`,
          caregiverUnitsCount: item.caregiverUnitsCount,
        };
        links.push(link);
        if (item.children) {
          item.children.forEach((childItem) => {
            const childItemUrlPathWithAddon = `${childItem.urlPath}-p`;
            const link: RelatedLink = {
              name: childItem.name,
              routerLink: ['/', RoutesEnums.SEARCH, selectedItemUrlPathWithAddon, childItemUrlPathWithAddon],
              title: `Hitta ${this.selectedItem().name} i ${childItem.name}`,
              caregiverUnitsCount: childItem.caregiverUnitsCount,
            };
            links.push(link);
          });
        }
      });
      return links;
    }
    return [];
  });
  private tools = inject(ToolsService);
  private defaults = inject(DefaultValuesService);
  private useState: SearchState = this.tools.clone(this.defaults.getDefaultSearchState());
  private subscriptions: Subscription[] = [];

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private relatedService: RelatedService,
  ) {
    effect(() => {
      if (this.selectedItem()) {
        this.relatedService.updateMetaTags(
          this.categoryType(),
          this.categoryTypeName(),
          this.selectedItem(),
          this.categoryAddon(),
        );
        this.relatedService.trackPageView(this.categoryType(), this.categoryTypeName(), this.selectedItem());
      }
    });
  }

  ngOnInit(): void {
    this.searchService.softResetState();
    this.searchService.resetWidgetState();
    this.onRouteUpdate();
    this.subscriptions.push(
      this.router.events.subscribe((event) => {
        if (event instanceof NavigationEnd) {
          this.onRouteUpdate();
        }
      }),
    );
  }

  public ngOnDestroy() {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }

  private onRouteUpdate(): void {
    this.categoryType.set(this.route.snapshot.params['categoryType']);
    this.categorySlug.set(this.route.snapshot.params['categorySlug'].replace(/(-c|-s|-t)$/, ''));
    this.setInitialValuesForCategoryType();
    this.searchService.reloadPlaces(this.useState);
    this.buildBreadcrumbs();
  }

  private setInitialValuesForCategoryType(): void {
    switch (this.categoryType()) {
      case RoutesAddonsEnums.RELATED_CARESERVICE:
        this.categoryTypeName.set(RoutesNamesEnum.RELATED_CARESERVICE);
        this.categoryDataKey.set('careServiceTypes');
        this.categoryAddon.set(UrlTermsSymbolsEnum.SERVICE_TYPE);
        this.useState.careServiceTypeUrlPath = this.categorySlug();
        break;
      case RoutesAddonsEnums.RELATED_SYMPTOM:
        this.categoryTypeName.set(RoutesNamesEnum.RELATED_SYMPTOM);
        this.categoryDataKey.set('symptoms');
        this.categoryAddon.set(UrlTermsSymbolsEnum.SYMPTOM);
        this.useState.symptomUrlPath = this.categorySlug();
        break;
      case RoutesAddonsEnums.RELATED_CAREPROVIDER:
      default:
        this.categoryTypeName.set(RoutesNamesEnum.RELATED_CAREPROVIDER);
        this.categoryDataKey.set('caregiverUnitTypes');
        this.categoryAddon.set(UrlTermsSymbolsEnum.CAREPROVIDER_UNIT_TYPE);
        this.useState.caregiverUnitTypeUrlPath = this.categorySlug();
    }
  }

  private buildBreadcrumbs() {
    this.breadcrumbs = [];

    this.breadcrumbs.push({
      routeParams: ['/', RoutesEnums.RELATED, this.categoryType()],
      routeQueryParams: null,
      anchor: this.categoryTypeName(),
    });

    if (this.selectedItem()) {
      this.breadcrumbs.push({
        routeParams: null,
        routeQueryParams: null,
        anchor: this.selectedItem().name,
      });
    }
  }
}
